import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, httpResource, type HttpResourceRef } from '@angular/common/http';
import type { Signal } from '@angular/core';
import { environment } from '../../../environments/environment';
import { map } from 'rxjs/operators';
import { Log } from './Profile.model';

export interface DemoModeResponse {
  demo_mode: boolean;
}

interface DemoModeWrappedResponse {
  data: DemoModeResponse;
}

function unwrapDemoModeResponse(response: DemoModeWrappedResponse | DemoModeResponse): DemoModeResponse {
  return 'data' in response ? response.data : response;
}

function parseDemoModeResource(raw: unknown): DemoModeResponse {
  const r = raw as { data?: DemoModeResponse } | DemoModeResponse;
  return 'data' in r ? r.data : (r as DemoModeResponse);
}

@Injectable()
export class ProfileService {
  constructor(private http: HttpClient) {}

  public SaveProfileData(userId, data) {
    const url = `${environment.customerUser}/${userId}/profile`;
    return this.http.put(url, data).pipe(map((response) => response));
  }

  public getSuperAdminData(user_id: string) {
    const url = `${environment.superAdminProperties}`;
    const params: HttpParams = new HttpParams().set('user_id', user_id);
    return this.http.get(url, { params: params }).pipe(map((response) => response));
  }

  public usersInCustomer(customerId) {
    const url = `${environment.customerUsers}`;
    const params: HttpParams = new HttpParams().set('customer_id', customerId);
    return this.http.get(url, { params }).pipe(map((response) => response));
  }

  public saveSuperAdminData(superAdminProperties) {
    const url = `${environment.superAdminProperties}`;
    return this.http.put(url, superAdminProperties).pipe(map((response) => response));
  }

  public getDemoModeResource(userId: Signal<number>): HttpResourceRef<DemoModeResponse> {
    return httpResource<DemoModeResponse>(
      () => ({
        url: `${environment.customerPath}v3/users/${userId()}/demo-mode`,
        reportProgress: true,
      }),
      { parse: parseDemoModeResource },
    );
  }

  public updateDemoMode(userId: number, demo_mode: boolean) {
    const url = `${environment.customerPath}v3/users/${userId}/demo-mode`;
    return this.http
      .put<DemoModeWrappedResponse | DemoModeResponse>(url, { demo_mode })
      .pipe(map(unwrapDemoModeResponse));
  }

  public changePassword(userId, body) {
    const url = `${environment.customerUser}/${userId}/profile_password`;
    return this.http.put(url, body).pipe(map((response) => response));
  }

  logUser(userId, customerId, clientIp) {
    const url = `${environment.customerById}/access/log`;
    const data: Log = {};
    data.userId = userId + '';
    data.customerId = customerId + '';
    data.ip = clientIp + '';

    return this.http.post(url, data).pipe(map((response) => response));
  }

  getClientIp() {
    const url = 'https://api.ipify.org?format=jsonp';

    return this.http.jsonp(url, 'callback').pipe(map((response) => response));
  }

  public setPeriodReport(payload) {
    const url = `${environment.customerPath}automatic-reports/customers/${payload.customer_id}/config`;
    const params: HttpParams = new HttpParams()
      .append('user_id', payload.user_id)
      .append('flag_daily', payload.flag_daily)
      .append('flag_weekly', payload.flag_weekly)
      .append('flag_monthly', payload.flag_monthly);

    return this.http.put(url, {}, { params }).pipe(map((response) => response));
  }

  public getPeriodReport(payload) {
    const url = `${environment.customerPath}automatic-reports/customers/${payload.customer_id}/users/${payload.user_id}`;

    return this.http.get(url).pipe(map((response) => response));
  }
}
